import { useState } from 'react';

import { savedViewsApi } from '@/api/queries/workspace';
import {
  Button,
  Field,
  Icon,
  IconButton,
  Input,
  ModalRoot,
  RadioGroup,
} from '@/components/ui';
import { cn } from '@/utils/cn';

import { CONTACT_SEARCH_FIELDS } from './ContactSearchFields';
import { parseQuery } from './parseQuery';
import type { ContactsSearch } from './searchTypes';

/** Human-readable chips for the active filter (the `q` query string). */
function filterChips(q: string | undefined): string[] {
  if (!q) return [];
  const labelByPath = new Map(CONTACT_SEARCH_FIELDS.map((f) => [f.value, f.label]));
  return parseQuery(q, CONTACT_SEARCH_FIELDS).conditions.map((c) => {
    const label = labelByPath.get(c.field) ?? c.field;
    if (c.operator === 'is_set' || c.operator === 'is_empty') {
      return `${label} ${c.operator === 'is_set' ? 'is set' : 'is empty'}`;
    }
    const value = Array.isArray(c.value) ? c.value.join(', ') : String(c.value ?? '');
    return `${label}: ${value}`;
  });
}

const COLORS = ['#1F47AB', '#0E8E8E', '#0F9D6B', '#D97706', '#C0392B', '#7C3AED', '#475569'];

interface SaveViewModalProps {
  open: boolean;
  onClose: () => void;
  currentSearch: ContactsSearch;
}

export function SaveViewModal({ open, onClose, currentSearch }: SaveViewModalProps) {
  const create = savedViewsApi.useCreate();
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [scope, setScope] = useState<'user' | 'workspace'>('user');
  const [error, setError] = useState<string | null>(null);

  // Reset the form whenever the modal transitions to open — adjusted during
  // render (the documented alternative to a setState-in-effect).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName('');
      setColor(COLORS[0]);
      setScope('user');
      setError(null);
    }
  }

  const submit = async () => {
    setError(null);
    if (!name.trim()) {
      setError('View name is required');
      return;
    }
    const filters: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(currentSearch)) {
      if (k === 'page' || k === 'pageSize' || k === 'new') continue;
      if (v !== undefined) filters[k] = v;
    }
    try {
      await create.mutateAsync({ name: name.trim(), color, scope, filters });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save view');
    }
  };

  // Render the chips that summarize what's being saved.
  const chips = filterChips(currentSearch.q);

  return (
    <ModalRoot open={open} onClose={onClose} size="sm" closeOnScrim>
      <div className="ds-modal-head">
        <h2>Save current filter as a view</h2>
        <IconButton icon="x" label="Close" className="ds-modal-close" onClick={onClose} />
      </div>
      <div className="ds-modal-body">
        <div className="ds-form-row">
          <Field label="View name" required>
            <Input
              autoFocus
              value={name}
              placeholder="My Lékaři, This week's leads…"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field label="Color">
            <div className="color-picker">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Color ${c}`}
                  className={cn('swatch', c === color && 'active')}
                  style={{ background: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </Field>

          <Field label="Visibility">
            <RadioGroup
              name="save-view-scope"
              value={scope}
              onChange={(v) => setScope(v)}
              options={[
                { value: 'user', label: 'Only me (sidebar shortcut)' },
                { value: 'workspace', label: 'Whole workspace (shared tab)' },
              ]}
            />
          </Field>

          <Field label="Filters being saved">
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 4,
                padding: 10,
                border: '1px solid var(--divider)',
                background: 'var(--surface-2)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 11.5,
              }}
            >
              {chips.length === 0 ? (
                <span style={{ color: 'var(--text-4)' }}>(no filters)</span>
              ) : (
                chips.map((c) => (
                  <span key={c} className="ds-tag brand">
                    {c}
                  </span>
                ))
              )}
            </div>
          </Field>

          {error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                background: 'var(--red-soft)',
                color: 'var(--red)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12.5,
              }}
            >
              <Icon name="alert" size={14} /> {error}
            </div>
          )}
        </div>
      </div>
      <div className="ds-modal-foot">
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={create.isPending}
          leftIcon={!create.isPending ? <Icon name="check" size={13} /> : undefined}
          onClick={() => void submit()}
        >
          Save view
        </Button>
      </div>
    </ModalRoot>
  );
}
